import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Modal } from 'antd';
import { CalculatorOutlined } from "@ant-design/icons";

const formatToTwoDecimals = (value) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return '0,00';
    return numericValue.toFixed(2).replace('.', ',');
};

const formatPrice = (price) => {
    // Convert to number if it's a string
    let numericPrice = parseFloat(price);

    // Check if the parsed value is a valid number
    if (isNaN(numericPrice)) {
        console.error("Invalid price value:", price);
        return "0,00"; // Return a default formatted value for invalid input
    }

    // Format the number with thousand separators and two decimal places
    return numericPrice
        .toFixed(2) // Fix to two decimal points
        .split('.') // Split integer and decimal parts
        .map((part, index) =>
            index === 0 ? part.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : part // Add space as thousand separator
        )
        .join(','); // Join integer and decimal parts
};

const FixedNumberInput = ({
    cannull = false,
    disabled,
    mode,
    value = 0,
    placeholder,
    addonAfter,
    onChange,
    calculator = false,
    maxvalue = Infinity,
    ...props
}) => {
    const [inputValue, setInputValue] = useState(() =>
        cannull && value === null ? '' : formatPrice(parseFloat(value ?? 0))
    );
    const equalsButtonRef = useRef(null); // Ref for the "=" button
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [calculation, setCalculation] = useState(formatPrice(value));
    const [result, setResult] = useState("");
    const [digits, setDigits] = useState(formatPrice(parseFloat(value ?? 0)).split(',')[0].replace(/\s/g, '').length);
    const inputRef = useRef(null);

    const handleKeyDown = (e) => {
        if (!isModalVisible) return;
    
        const allowedKeys = [
            "0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
            "+", "-", "*", "/", ",", "Enter", "Backspace"
        ];
    
        if (allowedKeys.includes(e.key)) {
            e.preventDefault(); // Prevent default behavior (e.g., form submission on Enter)
            if (e.key == "Enter") {
                equalsButtonRef.current?.click(); // Trigger "=" button click
            } else if (e.key === "Backspace") {
                setCalculation((prev) => prev.slice(0, -1));
            } else {
                setCalculation((prev) => prev + e.key);
            }
        }
    };


    useEffect(() => {
        setCalculation(formatPrice(value))
    }, [value]);
    
    useEffect(() => {
        if (isModalVisible) {
            window.addEventListener("keydown", handleKeyDown);
        } else {
            window.removeEventListener("keydown", handleKeyDown);
        }
    
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isModalVisible]);

    useEffect(() => {
        setInputValue(cannull && value === null ? '' : formatPrice(parseFloat(value ?? 0)));
    }, [value, cannull]);


    const handleInputChange = (e) => {
        if (!e || !e.target) return;
        let rawValue = e.target.value ?? '';
        const isRemoving = digits > rawValue.split(',')[0].replace(/\s/g, '').length
    
        let [integerPart, decimalPart] = rawValue.split(',');
        const isCursorOnLeftSide = e.target.selectionStart <= (integerPart ? integerPart.length : 0);
        const cursorPosition =
            e.target.selectionStart +
            (isRemoving ? (rawValue.split(',')[0].replace(/\s/g, '').length % 3 == 0 &&
            rawValue.split(',')[0].replace(/\s/g, '').length > 1 &&
            rawValue.split(',').length == 2 &&
            isCursorOnLeftSide
                ? -1
                : 0) : 
            (rawValue.split(',')[0].replace(/\s/g, '').length % 3 == 1 &&
            rawValue.split(',')[0].replace(/\s/g, '').length > 1 &&
            rawValue.split(',').length == 2 &&
            isCursorOnLeftSide
                ? 1
                : 0)
            );
    
        // Replace '.' with ',' and allow only numbers, commas, and spaces
        rawValue = rawValue.replace('.', ',').replace(/[^0-9, ]/g, '').replace(/ /g, '');
    
        // Replace ',' with '.' for numeric parsing
        let numericValue = parseFloat(rawValue.replace(',', '.'));
    
        if (!isNaN(numericValue)) {
            if (numericValue > maxvalue) numericValue = maxvalue;
            if (numericValue < 0) numericValue = 0;
            const fixedValue = formatPrice(numericValue);
            setInputValue(fixedValue);
            if (onChange) onChange(numericValue);
        } else if (rawValue === '' && cannull) {
            setInputValue('');
            if (onChange) onChange(null);
        } else if (rawValue === '') {
            setInputValue('');
            if (onChange) onChange(0);
        }
    
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        });
        setDigits(rawValue.split(',')[0].replace(/\s/g, '').length)
    };
    

    const handleBlur = () => {
        if (inputValue === '' && cannull) {
            if (onChange) onChange(null);
        } else if (inputValue === '' || isNaN(parseFloat(inputValue.replace(',', '.').replace(/ /g, '')))) {
            const defaultValue = formatPrice(0);
            setInputValue(defaultValue);
            if (onChange) onChange(0);
        } else {
            let numericValue = parseFloat(inputValue.replace(',', '.').replace(/ /g, ''));
            if (numericValue > maxvalue) numericValue = maxvalue;
            if (numericValue < 0) numericValue = 0;
            const fixedValue = formatPrice(numericValue);
            setInputValue(fixedValue);
            if (onChange) onChange(numericValue);
        }
    };

    const handleOpenModal = () => {
        setIsModalVisible(true);
    };

    function calculateFromString(input) {
        try {
            // Step 1: Remove spaces from the input
            const noSpacesInput = input.replace(/ /g, '');
    
            // Step 2: Replace commas with dots to correctly interpret decimals
            const convertedInput = noSpacesInput.replace(/,/g, '.');
    
            // Step 3: Validate the input string to allow only valid mathematical characters
            const validExpression = convertedInput.replace(/[^0-9+\-*/().]/g, '');
    
            if (!/^[0-9+\-*/().]+$/.test(validExpression)) {
                throw new Error('Invalid input');
            }
    
            // Step 4: Calculate the result using eval
            const result = eval(validExpression); // Note: Use eval cautiously with validated input
            return result;
        } catch (error) {
            console.error('Error calculating expression:', error.message);
            return 'Error in calculation';
        }
    }

    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    const handleButtonClick = (value) => {
        if (value === "=") {
            try {
                setCalculation(formatPrice(calculateFromString(calculation)))
                setResult(calculateFromString(calculation))
            } catch {
                setResult("Error");
            }
        } else if (value === "C") {
            setCalculation("");
            setResult("");
        } else {
            setCalculation((prev) => prev + value);
        }
    };

    const handleSave = () => {
        if (result !== "Error" && result !== "") {
            onChange(result);
            handleCloseModal();
        }
    };

    const renderCalculator = () => (
        <div style={{ textAlign: "center" }}>
            <Input
                value={calculation}
                readOnly
                style={{ marginBottom: "10px", fontSize: "16px" }}
                placeholder="Wprowadź liczbę"
            />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "5px" }}>
                {[
                    "7", "8", "9", "/",
                    "4", "5", "6", "*",
                    "1", "2", "3", "-",
                    "C", "0", ",", "=", "+"
                ].map((btn) => (
                    <Button
                        key={btn}
                        ref={btn === "=" ? equalsButtonRef : null} // Attach ref to "=" button
                        onClick={() => handleButtonClick(btn)}
                    >
                        {btn}
                    </Button>
                ))}
            </div>
            <Button
                type="primary"
                style={{ marginTop: "10px", width: "100%" }}
                onClick={handleSave}
                disabled={result === "" || result === "Error"}
            >
                Zatwierdź
            </Button>
        </div>
    );

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <Input
                type="text"
                disabled={disabled}
                ref={inputRef}
                value={inputValue}
                placeholder={placeholder}
                onChange={handleInputChange}
                onBlur={handleBlur}
                addonAfter={addonAfter ? addonAfter : false}
                {...props}
            />
            {calculator && mode !== 'preview' && (
                <Button
                    style={{ marginLeft: "8px", backgroundColor: "black", color: "white" }}
                    icon={<CalculatorOutlined />}
                    onClick={handleOpenModal}
                />
            )}
            <Modal
                title="Kalkulator"
                visible={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                centered
            >
                {renderCalculator()}
            </Modal>
        </div>
    );
};

export default FixedNumberInput;
